import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const { token, getAuthHeader } = useAuth();

  useEffect(() => {
    if (token) {
      fetchCart();
    } else {
      setCart({ items: [] });
    }
  }, [token]);

  const fetchCart = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: getAuthHeader()
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1, customizationNotes = null) => {
    if (!token) {
      throw new Error('Please login to add items to cart');
    }
    try {
      await axios.post(`${API}/cart/add`, {
        product_id: productId,
        quantity,
        customization_notes: customizationNotes
      }, {
        headers: getAuthHeader()
      });
      await fetchCart();
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.post(`${API}/cart/remove?product_id=${productId}`, {}, {
        headers: getAuthHeader()
      });
      await fetchCart();
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      await axios.post(`${API}/cart/clear`, {}, {
        headers: getAuthHeader()
      });
      setCart({ items: [] });
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw error;
    }
  };

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.items.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, loading, cartCount, cartTotal, addToCart, removeFromCart, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
